import { useEffect, useMemo, useRef, useState } from 'react';

const MAX_HIGHLIGHTS = 5000;

export function findTextMatches(text: string, query: string): Array<[number, number]> {
  const needle = query.trim().toLocaleLowerCase('th');
  if (!needle) return [];
  const haystack = text.toLocaleLowerCase('th');
  const matches: Array<[number, number]> = [];
  let cursor = 0;
  while (matches.length < MAX_HIGHLIGHTS) {
    const start = haystack.indexOf(needle, cursor);
    if (start < 0) break;
    matches.push([start, start + needle.length]);
    cursor = start + needle.length;
  }
  return matches;
}

function highlightedHtml(html: string, query: string): { html: string; count: number } {
  if (!query.trim() || typeof DOMParser === 'undefined') return { html, count: 0 };
  // The caller must sanitize rich HTML before passing it here. Text is inserted with
  // textContent, so a search term can never become markup.
  const document = new DOMParser().parseFromString(html, 'text/html');
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode as Text);
  let count = 0;
  for (const node of nodes) {
    const value = node.textContent ?? '';
    const matches = findTextMatches(value, query).slice(0, MAX_HIGHLIGHTS - count);
    if (!matches.length) continue;
    const fragment = document.createDocumentFragment();
    let cursor = 0;
    for (const [start, end] of matches) {
      fragment.append(document.createTextNode(value.slice(cursor, start)));
      const mark = document.createElement('mark');
      mark.dataset.searchHit = String(count++);
      mark.className = 'rounded-sm bg-amber-300 px-0.5 text-slate-950';
      mark.textContent = value.slice(start, end);
      fragment.append(mark);
      cursor = end;
    }
    fragment.append(document.createTextNode(value.slice(cursor)));
    node.replaceWith(fragment);
    if (count >= MAX_HIGHLIGHTS) break;
  }
  return { html: document.body.innerHTML, count };
}

interface SearchableTextPanelProps {
  text: string;
  title: string;
  initialQuery?: string;
  richHtml?: boolean;
  compact?: boolean;
}

export function SearchableTextPanel({
  text,
  title,
  initialQuery = '',
  richHtml = false,
  compact = false,
}: SearchableTextPanelProps) {
  const [query, setQuery] = useState(initialQuery);
  const [active, setActive] = useState(0);
  const viewport = useRef<HTMLDivElement>(null);

  const plainMatches = useMemo(
    () => (richHtml ? [] : findTextMatches(text, query)),
    [richHtml, text, query],
  );
  const richResult = useMemo(
    () => (richHtml ? highlightedHtml(text, query) : { html: '', count: 0 }),
    [richHtml, text, query],
  );
  const count = richHtml ? richResult.count : plainMatches.length;
  const selected = Math.min(active, Math.max(0, count - 1));

  useEffect(() => {
    const container = viewport.current;
    if (!container || !count) return;
    const marks = container.querySelectorAll<HTMLElement>('[data-search-hit]');
    marks.forEach((mark) => {
      const isActive = mark.dataset.searchHit === String(selected);
      mark.classList.toggle('bg-orange-400', isActive);
      mark.classList.toggle('ring-2', isActive);
      mark.classList.toggle('ring-orange-600', isActive);
    });
    const current = marks[selected];
    if (current) {
      container.scrollTop += current.getBoundingClientRect().top - container.getBoundingClientRect().top - 32;
    }
  }, [count, selected, query, text]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          aria-label={`ค้นใน${title}`}
          className="field min-w-0 flex-1 text-sm"
          placeholder={`ค้นใน${title}`}
          value={query}
          onChange={(event) => { setQuery(event.target.value); setActive(0); }}
        />
        {query.trim() && (
          <span className="text-xs text-slate-600" role="status">
            {count ? `${selected + 1}/${count}${count === MAX_HIGHLIGHTS ? '+' : ''}` : 'ไม่พบคำค้น'}
          </span>
        )}
        <button type="button" className="btn-secondary px-2 py-1 text-xs" aria-label={`ผลก่อนหน้าใน${title}`} disabled={!count} onClick={() => setActive((selected - 1 + count) % count)}>↑</button>
        <button type="button" className="btn-secondary px-2 py-1 text-xs" aria-label={`ผลถัดไปใน${title}`} disabled={!count} onClick={() => setActive((selected + 1) % count)}>↓</button>
      </div>
      <div ref={viewport} className={`${compact ? 'max-h-40' : 'max-h-80'} overflow-auto rounded-lg border border-slate-300 bg-white p-3 text-slate-900`}>
        {!text ? (
          <p className="text-xs text-slate-600">ยังไม่มีข้อความ</p>
        ) : richHtml ? (
          <div className="break-words text-sm" dangerouslySetInnerHTML={{ __html: richResult.html }} />
        ) : (
          <pre className="whitespace-pre-wrap break-words font-sans text-xs leading-relaxed">
            {plainMatches.length === 0 ? text : plainMatches.map(([start, end], index) => (
              <span key={start}>
                {text.slice(index === 0 ? 0 : plainMatches[index - 1][1], start)}
                <mark data-search-hit={index} className="rounded-sm bg-amber-300 px-0.5 text-slate-950">{text.slice(start, end)}</mark>
              </span>
            ))}
            {plainMatches.length > 0 && text.slice(plainMatches.at(-1)![1])}
          </pre>
        )}
      </div>
    </div>
  );
}
